// Reine Funktionen des Buddy-Chats — ohne DOM, per `node --test` prüfbar.
// Spec: docs/superpowers/specs/2026-09-16-radiology-buddy-recherche-design.md

export const MAX_ZUEGE = 20;

// Der Chat überlebt Schließen und Neuladen; verworfen wird er nur per Reset
// oder mit der Nacht um 03:00 (lokale Zeit).
export const NACHT_STUNDE = 3;
export const MAX_NACHRICHTEN = 100;
const MAX_NACHRICHT_ZEICHEN = 20000;

function naechsteNacht(jetzt) {
  const d = new Date(jetzt);
  d.setHours(NACHT_STUNDE, 0, 0, 0);
  if (d <= jetzt) d.setDate(d.getDate() + 1);
  return d;
}

// Der letzte 03:00-Zeitpunkt vor `jetzt`: alles davor ist von gestern.
export function letzteNacht(jetzt = new Date()) {
  const d = naechsteNacht(jetzt);
  d.setDate(d.getDate() - 1);
  return d;
}

export function msBisNacht(jetzt = new Date()) {
  return naechsteNacht(jetzt).getTime() - jetzt.getTime();
}

export function chatStandSchreiben(nachrichten, verlauf, jetzt = new Date()) {
  return JSON.stringify({
    stand: jetzt.getTime(),
    nachrichten: (nachrichten || [])
      .slice(-MAX_NACHRICHTEN)
      .map((n) => ({ typ: n.typ, text: String(n.text ?? '').slice(0, MAX_NACHRICHT_ZEICHEN) })),
    verlauf: (verlauf || []).slice(-MAX_ZUEGE),
  });
}

// Gibt den gespeicherten Chat zurück — oder null, wenn nichts da, etwas
// unlesbar oder der Stand älter als die letzte Nacht ist.
export function chatStandLesen(roh, jetzt = new Date()) {
  let stand;
  try {
    stand = JSON.parse(roh ?? 'null');
  } catch {
    return null;
  }
  if (!stand || typeof stand.stand !== 'number') return null;
  if (stand.stand < letzteNacht(jetzt).getTime()) return null;
  const nachrichten = (Array.isArray(stand.nachrichten) ? stand.nachrichten : [])
    .filter((n) => n && typeof n.text === 'string' && typeof n.typ === 'string')
    .slice(-MAX_NACHRICHTEN);
  if (nachrichten.length === 0) return null;
  const verlauf = (Array.isArray(stand.verlauf) ? stand.verlauf : [])
    .filter((z) => z && typeof z.text === 'string' && (z.rolle === 'nutzer' || z.rolle === 'buddy'))
    .slice(-MAX_ZUEGE);
  return { nachrichten, verlauf };
}

// Schalter unter dem Eingabefeld: was der Buddy als Kontext bekommt.
export const SCHALTER_STANDARD = Object.freeze({ befund: true, vault: true, literatur: true });

// Gespeicherten Zustand lesen; Unlesbares oder Fehlendes faellt auf „an".
export function schalterLesen(roh) {
  let gespeichert = {};
  try {
    gespeichert = JSON.parse(roh ?? '{}') ?? {};
  } catch {
    gespeichert = {};
  }
  const schalter = { ...SCHALTER_STANDARD };
  for (const k of Object.keys(schalter)) {
    if (typeof gespeichert[k] === 'boolean') schalter[k] = gespeichert[k];
  }
  return schalter;
}

export function anfrageBody(nachricht, befund, verlauf, schalter = SCHALTER_STANDARD) {
  return {
    nachricht,
    befund: schalter.befund ? befund || '' : '',
    verlauf: (verlauf || []).slice(-MAX_ZUEGE),
    vault: schalter.vault,
    literatur: schalter.literatur,
  };
}

// NDJSON kommt in beliebigen Stücken; eine Zeile ist erst mit '\n' vollständig.
export function ndjsonPuffer() {
  let rest = '';
  const parse = (zeilen) => zeilen.filter((z) => z.trim()).map((z) => JSON.parse(z));
  return {
    zeilen(chunk) {
      rest += chunk;
      const teile = rest.split('\n');
      rest = teile.pop();
      return parse(teile);
    },
    ende() {
      const letzte = rest;
      rest = '';
      return parse([letzte]);
    },
  };
}

// Titel aus fremden APIs dürfen die Markdown-Links nicht zerlegen.
function sicher(text) {
  return String(text ?? '').replace(/[\[\]<>]/g, '').replace(/\s+/g, ' ').trim();
}

/// Die Belege als EINE Zeile unter der Antwort, durch `·` getrennt.
///
/// Bis zum 17.09.2026 war das eine Aufzählung mit Überschrift „Quellen" —
/// bei sechs Belegen nahm sie mehr Platz ein als die Antwort. Die Zeile
/// beginnt mit „Quellen:", daran erkennt `quellenZeileMarkieren` sie und
/// setzt sie kleiner.
export function quellenMarkdown(belege) {
  if (!belege || belege.length === 0) return '';
  const teile = belege.map((b) => {
    if (b.art === 'vault') {
      const titel = b.titel ? ` ${sicher(b.titel)}` : '';
      return `${b.kennung}${titel} [${b.pfad}:${b.zeile}]`;
    }
    const label = `${sicher(b.titel)}${b.jahr ? ` (${b.jahr})` : ''}`;
    const url = b.pmid
      ? `https://pubmed.ncbi.nlm.nih.gov/${encodeURIComponent(b.pmid)}/`
      : b.doi
        ? `https://doi.org/${encodeURI(b.doi)}`
        : null;
    return `${b.kennung} ${url ? `[${label}](${url})` : label}`;
  });
  return `\n\nQuellen: ${teile.join(' · ')}`;
}

export function hinweiseMarkdown(hinweise) {
  if (!hinweise || hinweise.length === 0) return '';
  return hinweise.map((h) => `\n\n_${h}_`).join('');
}

// Sendesperre und Generation: solange ein Strom läuft, startet kein zweiter
// (`starten()` gibt dann null). `leeren()` — Verlauf zurücksetzen — entwertet
// die laufende Generation, damit eine spät eintreffende Antwort den geleerten
// Verlauf nicht wieder befüllt (`gilt(g)` wird false).
export function buddyStrom() {
  let laeuft = false;
  let generation = 0;
  return {
    laeuft: () => laeuft,
    starten() {
      if (laeuft) return null;
      laeuft = true;
      return generation;
    },
    beenden() {
      laeuft = false;
    },
    leeren() {
      generation += 1;
    },
    gilt: (g) => g === generation,
  };
}
